import React from 'react';
import { defineMessages, useIntl, FormattedMessage, type MessageDescriptor } from 'react-intl-next';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Inline, xcss } from '@atlaskit/primitives';
import Button from '@atlaskit/button/new';

const i18n = defineMessages({
	legendColorAriaLabel: {
		id: 'confluence-analytics.metric-selector.legend-color-aria-label',
		defaultMessage: 'Color legend for {metric}',
		description:
			'Non-visible aria label for the small legend box where users can see the color of given metric next to its label.',
	},
});

export type MetricConfigType = {
	enabled: boolean;
	title: MessageDescriptor;
	color: string;
};

const legendSquareStyle = xcss({
	width: '10px',
	height: '10px',
	marginInlineEnd: 'space.075',
	borderRadius: 'radius.xsmall',
});

const LegendSquare = ({ backgroundColor, ...props }) => (
	<Box {...props} xcss={legendSquareStyle} style={{ backgroundColor }} />
);

export const MetricSelector = <MetricType extends string>({
	value,
	onChange,
}: {
	value: Record<MetricType, MetricConfigType>;
	onChange: (metricChanged: string, newValue: Record<MetricType, MetricConfigType>) => void;
}) => {
	const { formatMessage } = useIntl();

	const selectedMetrics = Object.keys(value).filter((key) => value[key].enabled);

	return (
		<>
			{Object.entries<MetricConfigType>(value).map(([key, config]) => (
				<Button
					key={key}
					isSelected={config.enabled}
					isDisabled={selectedMetrics.length === 1 && selectedMetrics[0] === key}
					onClick={() => {
						onChange(key, {
							...value,
							[key]: {
								...value[key],
								enabled: !value[key].enabled,
							},
						});
					}}
				>
					<Inline alignBlock="center">
						<LegendSquare
							backgroundColor={config.color}
							aria-label={formatMessage(i18n.legendColorAriaLabel, {
								metric: formatMessage(config.title),
							})}
						/>
						<FormattedMessage {...config.title} />
					</Inline>
				</Button>
			))}
		</>
	);
};
